import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Fire, TrendUp, UsersThree, House, ArrowRight, Warning, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { TierBadge, timeAgo } from "./helpers";

function StatCard({ icon: Icon, label, value, sub, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className="bg-[#101116] border border-white/5 rounded-sm p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-white/40 uppercase tracking-[0.18em]">{label}</span>
        <Icon size={18} weight="duotone" className="text-brand" />
      </div>
      <p className="text-4xl text-white font-serif">{value}</p>
      {sub && <p className="text-xs text-white/40 mt-2">{sub}</p>}
    </motion.div>
  );
}

function feedAgeHours(feeds) {
  const stamps = [
    feeds?.borough_sync?.synced_at,
    feeds?.borough_rentals_sync?.synced_at,
  ].filter(Boolean);
  if (!stamps.length) return null;
  const newest = stamps
    .map((s) => Date.parse(s))
    .filter((n) => !Number.isNaN(n))
    .sort((a, b) => b - a)[0];
  if (!newest) return null;
  return (Date.now() - newest) / 36e5;
}

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [cronHealth, setCronHealth] = useState(null);
  const [feeds, setFeeds] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [s, l, h, f] = await Promise.all([
        api.get("/stats"),
        api.get("/leads"),
        api.get("/health/crons").catch(() => ({ data: null })),
        api.get("/feeds/status").catch(() => ({ data: null })),
      ]);
      setStats(s.data);
      setRecent(l.data.slice(0, 6));
      setCronHealth(h.data);
      setFeeds(f.data);
    };
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const staleJobs = cronHealth?.crons
    ? Object.entries(cronHealth.crons).filter(([, v]) => v.stale).map(([k]) => k)
    : [];
  const ageH = feedAgeHours(feeds);
  const listingsStale = ageH != null && ageH > 72;
  const usage = feeds?.borough_api?.usage;
  const usageHot = Boolean(usage?.optional_blocked || usage?.hard_blocked);

  const refreshListings = async () => {
    setSyncing(true);
    try {
      await api.post("/admin/sync-listings", null, { params: { force: true } });
      const f = await api.get("/feeds/status");
      setFeeds(f.data);
      toast.success("Listing refresh requested");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="overview-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl text-white">Overview</h1>
          <p className="text-white/50 text-sm mt-1">Your pipeline at a glance.</p>
        </div>
        <Link
          to="/dashboard/leads"
          className="hidden sm:flex items-center gap-2 text-sm text-brand hover:gap-3 transition-all"
          data-testid="view-all-leads"
        >
          View all leads <ArrowRight size={16} weight="bold" />
        </Link>
      </div>

      {usage && (
        <div
          className={`rounded-sm border p-4 flex flex-wrap items-center gap-3 ${
            usageHot ? "border-amber-400/30 bg-amber-500/10" : "border-white/10 bg-[#101116]"
          }`}
          data-testid="borough-usage-strip"
        >
          <span className="text-xs uppercase tracking-wider text-white/40">Borough API this month</span>
          <p className="text-sm text-white/80 flex-1 min-w-[12rem]" data-testid="borough-usage-counts">
            {usage.used} / {usage.cap} requests used
            {usage.optional_blocked ? " · photos & detail paused (soft cap)" : ""}
            {usage.hard_blocked ? " · sync paused (hard cap)" : ""}
          </p>
          {usage.by_kind && Object.keys(usage.by_kind).length > 0 && (
            <span className="text-xs text-white/40">
              {Object.entries(usage.by_kind).map(([k, v]) => `${k}:${v}`).join(" · ")}
            </span>
          )}
        </div>
      )}

      {(staleJobs.length > 0 || listingsStale) && (
        <div
          className="rounded-sm border border-amber-400/30 bg-amber-500/10 p-4 flex flex-wrap items-center gap-3"
          data-testid="ops-stale-strip"
        >
          <Warning size={18} weight="duotone" className="text-amber-300" />
          <p className="text-sm text-white/80 flex-1 min-w-[12rem]">
            {listingsStale && ageH != null
              ? `Public listings last refreshed about ${Math.round(ageH)} hours ago.`
              : "Background jobs look stale."}
            {staleJobs.length > 0 ? ` Waiting: ${staleJobs.join(", ")}.` : ""}
          </p>
          <button
            type="button"
            data-testid="overview-refresh-listings"
            onClick={refreshListings}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 rounded-sm border border-white/15 text-xs uppercase tracking-wider text-white/80 hover:text-white disabled:opacity-60"
          >
            <ArrowsClockwise size={14} className={syncing ? "animate-spin" : ""} />
            {syncing ? "Refreshing…" : "Refresh listings"}
          </button>
          <Link to="/dashboard/settings" className="text-xs text-brand hover:underline" data-testid="overview-settings-link">
            Settings
          </Link>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard icon={UsersThree} label="Total Leads" value={stats?.total ?? "—"} sub={`${stats?.new_count ?? 0} new`} delay={0} />
        <StatCard icon={Fire} label="Hot Leads" value={stats?.by_tier?.hot ?? "—"} sub="Priority outreach" delay={0.05} />
        <StatCard icon={House} label="Buyers / Sellers" value={`${stats?.by_type?.buyer ?? 0} / ${stats?.by_type?.seller ?? 0}`} delay={0.1} />
        <StatCard icon={TrendUp} label="Conversion" value={`${stats?.conversion ?? 0}%`} sub={`${stats?.by_status?.closed ?? 0} closed`} delay={0.15} />
      </div>

      {stats?.today && (stats.today.follow_ups_due > 0 || stats.today.bookings_upcoming > 0 || stats.today.hot_new > 0) && (
        <div
          className="rounded-sm border border-brand/30 bg-brand/5 p-4 flex flex-wrap items-center gap-x-6 gap-y-2"
          data-testid="crm-today-strip"
        >
          <span className="text-xs uppercase tracking-wider text-brand font-medium">Today</span>
          {stats.today.follow_ups_due > 0 && (
            <Link to="/dashboard/leads" className="text-sm text-white hover:text-brand">
              {stats.today.follow_ups_due} follow-up{stats.today.follow_ups_due > 1 ? "s" : ""} due
            </Link>
          )}
          {stats.today.bookings_upcoming > 0 && (
            <span className="text-sm text-white/80">
              {stats.today.bookings_upcoming} call{stats.today.bookings_upcoming > 1 ? "s" : ""} booked (next 36h)
            </span>
          )}
          {stats.today.hot_new > 0 && (
            <Link to="/dashboard/leads" className="text-sm text-white/80 hover:text-brand">
              {stats.today.hot_new} hot lead{stats.today.hot_new > 1 ? "s" : ""} awaiting contact
            </Link>
          )}
        </div>
      )}

      {stats?.prospects?.total > 0 && (
        <div
          className="bg-[#101116] border border-white/5 rounded-sm p-5 flex flex-wrap items-center gap-x-8 gap-y-2"
          data-testid="prospect-pipeline-stats"
        >
          <span className="text-[11px] uppercase tracking-wider text-white/35">Prospect pipeline</span>
          <span className="text-sm text-white/70">{stats.prospects.total} mined</span>
          <span className="text-sm text-white/70">{stats.prospects.sent} contacted</span>
          <span className={`text-sm ${stats.prospects.replied > 0 ? "text-brand" : "text-white/70"}`}>
            {stats.prospects.replied} replied
          </span>
          <span className={`text-sm ${stats.prospects.converted > 0 ? "text-brand" : "text-white/70"}`}>
            {stats.prospects.converted} converted to leads
          </span>
          <Link to="/dashboard/prospects" className="text-sm text-brand hover:underline ml-auto" data-testid="view-prospects-link">
            Open prospects
          </Link>
        </div>
      )}

      {/* Tier distribution */}
      <div className="grid lg:grid-cols-3 gap-5">
        {["hot", "warm", "cold"].map((tier) => {
          const count = stats?.by_tier?.[tier] ?? 0;
          const total = stats?.total || 1;
          const pct = Math.round((count / total) * 100);
          return (
            <div key={tier} className="bg-[#101116] border border-white/5 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <TierBadge tier={tier} />
                <span className="text-2xl text-white font-serif">{count}</span>
              </div>
              <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${pct}%`,
                    background: tier === "hot" ? "#E26D5A" : tier === "warm" ? "#D4AF37" : "#7BA1B0",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent leads */}
      <div className="bg-[#101116] border border-white/5 rounded-sm">
        <div className="px-6 py-4 border-b border-white/5">
          <h2 className="text-lg text-white">Recent leads</h2>
        </div>
        {recent.length === 0 ? (
          <p className="px-6 py-12 text-center text-white/40 text-sm">
            No leads yet. Share your funnel link to start capturing leads.
          </p>
        ) : (
          <div className="divide-y divide-white/5">
            {recent.map((l) => (
              <Link
                key={l.id}
                to={`/dashboard/leads/${l.id}`}
                data-testid={`recent-lead-${l.id}`}
                className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-white/5 transition-colors"
              >
                <div className="min-w-0">
                  <p className="text-white truncate">{l.name}</p>
                  <p className="text-xs text-white/40 capitalize">
                    {l.lead_type} · {l.timeline || "—"} · {timeAgo(l.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {l.score_numeric != null && (
                    <span className="text-sm text-white/50 tabular-nums">{l.score_numeric}</span>
                  )}
                  <TierBadge tier={l.score_tier} pending={l.ai_status === "pending"} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
